import React, { memo, useCallback, useState, useEffect } from 'react';
import { AutoAwesome } from '@mui/icons-material';
import type { AIFormState } from '@trinity/chat';
import { AITooltip } from '@trinity/chat-components/components/Chat/KolSearch.Profile.AITooltip';
import type { apiSchemas } from '@trinity/management-app-client';
import { renderDynamicField } from './DynamicForm.form.fields';
import { SubmittedView } from './DynamicForm.form.submitted';
import { validateForm } from './DynamicForm.form.validation';
import {
  FormContainer,
  FormHeader,
  FormTitle,
  FormGrid,
  ErrorMessage,
  ActionButtonContainer,
  ActionButton,
  SectionContainer,
  SectionTitle,
  SectionDivider,
  THEME,
} from './DynamicForm.styles.index';

const AI_ANIMATION = {
  DURATION: 1500,
  PHASES: {
    IDLE: 'idle' as const,
    PENDING: 'pending' as const,
    ANIMATING: 'animating' as const,
    APPLIED: 'applied' as const,
  },
} as const;

type FormSchema = apiSchemas.FormSchema;
type FieldDefinition = apiSchemas.FieldDefinition;
type AnimationPhase = (typeof AI_ANIMATION.PHASES)[keyof typeof AI_ANIMATION.PHASES];
type FormFieldValue = string | string[] | null | undefined;

function applyAICorrections(
  aiState: AIFormState | undefined,
  correctionsApplied: boolean,
  onAICorrection: ((fieldName: string, originalValue: unknown, correctedValue: unknown) => void) | undefined,
  setAnimationPhase: (phase: AnimationPhase) => void,
  setInternalCorrectionsApplied: (v: boolean) => void,
  setAnimationTimer: (v: NodeJS.Timeout | null) => void
): (() => void) | undefined {
  if (!aiState || !aiState.patternInsights || aiState.isProcessing) {
    return;
  }

  const fieldsNeedingCorrection = aiState.patternInsights.filter(
    (insight) => insight.hasCorrectionHistory && !correctionsApplied
  );

  if (fieldsNeedingCorrection.length === 0) {
    return;
  }

  setAnimationPhase(AI_ANIMATION.PHASES.ANIMATING);

  const timer = setTimeout(() => {
    if (aiState.correctedData && onAICorrection) {
      fieldsNeedingCorrection.forEach((insight) => {
        const correctedValue = aiState.correctedData?.[insight.field];
        if (correctedValue !== undefined && correctedValue !== insight.originalValue) {
          onAICorrection(insight.field, insight.originalValue, correctedValue);
        }
      });
    }

    setInternalCorrectionsApplied(true);
    setAnimationPhase(AI_ANIMATION.PHASES.APPLIED);
  }, AI_ANIMATION.DURATION);

  setAnimationTimer(timer);

  return () => {
    clearTimeout(timer);
  };
}

const AIProcessingIndicator = () => (
  <div
    style={{
      padding: '8px 12px',
      backgroundColor: '#e3f2fd',
      border: '1px solid #2196f3',
      borderRadius: '4px',
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    }}
  >
    <div
      className="ai-spin"
      style={{
        width: '16px',
        height: '16px',
        border: '2px solid #2196f3',
        borderTop: '2px solid transparent',
        borderRadius: '50%',
      }}
    />
    {CONSTANTS.MESSAGES.AI_PROCESSING}
  </div>
);

const AICorrectionsSummary = () => (
  <div
    style={{
      fontSize: THEME.fontSize.xs,
      color: '#6b7280',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    }}
  >
    <AITooltip helpText={CONSTANTS.MESSAGES.AI_CORRECTIONS_SUMMARY} />
  </div>
);

const resolveDefaultTitle = (
  customTitle: string | undefined,
  isSubmitted: boolean,
  isDraft: boolean,
  readOnly: boolean,
  hasPrefilledData: boolean,
  formConfig: FormSchema
): string => {
  if (customTitle) return customTitle;
  if (isSubmitted) return CONSTANTS.DEFAULT_TITLES.SUBMITTED;
  if (isDraft) return CONSTANTS.DEFAULT_TITLES.DRAFT;
  if (readOnly) return CONSTANTS.DEFAULT_TITLES.EDITING;
  if (hasPrefilledData) return CONSTANTS.DEFAULT_TITLES.PREFILLED;
  return formConfig.prompt?.initialMessage || CONSTANTS.DEFAULT_TITLES.EMPTY;
};

const groupFieldsBySections = (
  sortedFields: FieldDefinition[],
  sections: { id: string; order: number; showInChat: boolean }[]
) => {
  const fieldsBySection = new Map<string, FieldDefinition[]>();
  const fieldsWithoutSection: FieldDefinition[] = [];

  sortedFields.forEach((field) => {
    const isHiddenSystemField =
      (field.type === 'static' || field.type === 'variable') && field.visibleToUser === false;
    if (isHiddenSystemField) {
      return;
    }

    if (field.sectionId) {
      const section = sections.find((s) => s.id === field.sectionId);
      if (section) {
        if (!fieldsBySection.has(field.sectionId)) {
          fieldsBySection.set(field.sectionId, []);
        }
        fieldsBySection.get(field.sectionId)?.push(field);
      } else {
        fieldsWithoutSection.push(field);
      }
    } else {
      fieldsWithoutSection.push(field);
    }
  });

  return { fieldsBySection, fieldsWithoutSection };
};

const prepareSections = (formConfig: FormSchema, sortedFields: FieldDefinition[]) => {
  const normalizedSections = (formConfig.sections || []).map((section) => ({
    ...section,
    showInChat: Boolean(section.showInChat),
  }));
  const sections = normalizedSections
    .filter((section) => section.showInChat)
    .sort((a, b) => a.order - b.order);
  const { fieldsBySection, fieldsWithoutSection } = groupFieldsBySections(sortedFields, sections);
  return { sections, fieldsBySection, fieldsWithoutSection };
};

const getAIProcessingIndicator = (aiState: AIFormState | undefined): React.ReactNode => {
  if (aiState?.isProcessing) {
    return <AIProcessingIndicator />;
  }
  return null;
};

const getAICorrectionsSummary = (aiState: AIFormState | undefined): React.ReactNode => {
  if (aiState && !aiState.isProcessing) {
    return <AICorrectionsSummary />;
  }
  return null;
};

const FormActionButtons: React.FC<{
  onSubmit?: () => void;
  onSave?: () => void;
  onDraft?: () => void;
  onCancel?: () => void;
  onEnhance?: () => void;
  handleSave: () => void;
  handleDraft: () => void;
  isSubmitting: boolean;
  isEnhancing?: boolean;
  finalIsValid: boolean;
  isReadOnlyMode: boolean;
}> = ({
  onSubmit,
  onSave,
  onDraft,
  onCancel,
  onEnhance,
  handleSave,
  handleDraft,
  isSubmitting,
  isEnhancing,
  finalIsValid,
  isReadOnlyMode,
}) => (
  <ActionButtonContainer>
    {onEnhance && (
      <ActionButton
        onClick={onEnhance}
        variant="primary"
        disabled={isEnhancing || isSubmitting}
        type="button"
        icon={AutoAwesome}
      >
        {isEnhancing ? 'Enhancing...' : 'Enhance with AI'}
      </ActionButton>
    )}
    {onSubmit && (
      <ActionButton
        onClick={() => {}}
        variant="primary"
        disabled={isSubmitting || !finalIsValid}
        type="submit"
      >
        {isSubmitting ? CONSTANTS.MESSAGES.SAVING : CONSTANTS.MESSAGES.SAVE}
      </ActionButton>
    )}
    {onSave && !onSubmit && (
      <ActionButton
        onClick={handleSave}
        variant="tertiary"
        disabled={isSubmitting || !finalIsValid}
        type="button"
      >
        {isSubmitting ? CONSTANTS.MESSAGES.SAVING : CONSTANTS.MESSAGES.SAVE}
      </ActionButton>
    )}
    {onDraft && !isReadOnlyMode && (
      <ActionButton
        onClick={handleDraft}
        variant="primary"
        disabled={isSubmitting}
        type="button"
        aria-label="Save as Draft"
      >
        {CONSTANTS.MESSAGES.SAVE_AS_DRAFT}
      </ActionButton>
    )}
    {onCancel && (
      <ActionButton variant="secondary" onClick={onCancel} disabled={isSubmitting} type="button">
        {CONSTANTS.MESSAGES.CANCEL}
      </ActionButton>
    )}
  </ActionButtonContainer>
);

const CONSTANTS = {
  DEFAULT_TITLES: {
    PREFILLED: "I've analyzed your text and pre-filled the form. Please review and update as needed:",
    EMPTY: 'Please fill out the form below:',
    EDITING: 'Edit Form',
    DRAFT: 'Draft Form',
    SUBMITTED: 'Submitted Form',
  },
  MESSAGES: {
    SAVING: 'Saving...',
    SAVE: 'Save',
    SAVE_AS_DRAFT: 'Save as Draft',
    CANCEL: 'Cancel',
    AI_PROCESSING: 'AI is analyzing and extracting data...',
    AI_CORRECTIONS_SUMMARY: `This AI system automatically analyzes your input text and:

• **Extracts relevant information** from your description
• **Pre-fills form fields** with detected data
• **Validates data consistency** across form fields`,
  },
} as const;

export interface DynamicFormProps {
  formConfig: FormSchema;
  formData: Record<string, unknown>;
  onFieldChange: (fieldName: string, value: unknown) => void;
  onSubmit?: () => void;
  onCancel?: () => void;
  onSave?: () => void;
  onDraft?: () => void;
  onEdit?: () => void;
  onEnhance?: () => void;
  onDownloadPdf?: () => Promise<void>;
  isValid?: boolean;
  isSubmitting?: boolean;
  isEnhancing?: boolean;
  isDownloadingPdf?: boolean;
  isSubmitted?: boolean;
  isDraft?: boolean;
  submitError?: string | null;
  hasPrefilledData?: boolean;
  title?: string;
  customTitle?: string;
  showButtons?: boolean;
  readOnly?: boolean;
  aiState?: AIFormState;
  onAICorrection?: (fieldName: string, originalValue: unknown, correctedValue: unknown) => void;
  correctionsApplied?: boolean;
  className?: string;
  style?: React.CSSProperties;
  submittedAt?: string | number | Date;
  draftAt?: string | number | Date;
}

const DynamicFormBody: React.FC<{
  className?: string;
  style?: React.CSSProperties;
  title?: string;
  defaultTitle: string;
  aiProcessingIndicator: React.ReactNode;
  handleSubmit: (e: React.FormEvent) => void;
  sections: { id: string; title: string; order: number; showInChat: boolean }[];
  fieldsBySection: Map<string, FieldDefinition[]>;
  renderSectionFields: (fields: FieldDefinition[], readOnly: boolean) => React.ReactNode;
  isReadOnlyMode: boolean;
  fieldsWithoutSection: FieldDefinition[];
  renderFieldsWithoutSections: (readOnly: boolean) => React.ReactNode;
  aiCorrectionsSummary: React.ReactNode;
  submitError?: string | null;
  showButtons: boolean;
  onSubmit?: () => void;
  onSave?: () => void;
  onDraft?: () => void;
  onCancel?: () => void;
  onEnhance?: () => void;
  handleSave: () => void;
  handleDraft: () => void;
  isSubmitting: boolean;
  isEnhancing?: boolean;
  finalIsValid: boolean;
}> = ({
  className,
  style,
  title,
  defaultTitle,
  aiProcessingIndicator,
  handleSubmit,
  sections,
  fieldsBySection,
  renderSectionFields,
  isReadOnlyMode,
  fieldsWithoutSection,
  renderFieldsWithoutSections,
  aiCorrectionsSummary,
  submitError,
  showButtons,
  onSubmit,
  onSave,
  onDraft,
  onCancel,
  onEnhance,
  handleSave,
  handleDraft,
  isSubmitting,
  isEnhancing,
  finalIsValid,
}) => (
  <FormContainer
    className={className}
    style={{
      minWidth: '600px',
      maxWidth: '600px',
      margin: '0 auto',
      ...style,
    }}
  >
    <FormHeader>
      <FormTitle
        style={{
          maxWidth: '100%',
          wordWrap: 'break-word',
          overflowWrap: 'break-word',
          hyphens: 'auto',
          whiteSpace: 'normal',
        }}
      >
        {title || defaultTitle}
      </FormTitle>
    </FormHeader>

    {aiProcessingIndicator}

    <form onSubmit={handleSubmit} noValidate>
      <FormGrid>
        {sections.map((section) => {
          const sectionFields = fieldsBySection.get(section.id) || [];
          if (sectionFields.length === 0) return null;

          return (
            <SectionContainer key={section.id}>
              <SectionTitle>{section.title}</SectionTitle>
              <SectionDivider />
              <FormGrid>{renderSectionFields(sectionFields, isReadOnlyMode)}</FormGrid>
            </SectionContainer>
          );
        })}

        {fieldsWithoutSection.length > 0 && renderFieldsWithoutSections(isReadOnlyMode)}
      </FormGrid>

      {aiCorrectionsSummary}

      {submitError && (
        <ErrorMessage role="alert" aria-live="assertive">
          {submitError}
        </ErrorMessage>
      )}

      {showButtons && (
        <FormActionButtons
          onSubmit={onSubmit}
          onSave={onSave}
          onDraft={onDraft}
          onCancel={onCancel}
          onEnhance={onEnhance}
          handleSave={handleSave}
          handleDraft={handleDraft}
          isSubmitting={isSubmitting}
          isEnhancing={isEnhancing}
          finalIsValid={finalIsValid}
          isReadOnlyMode={isReadOnlyMode}
        />
      )}
    </form>

    <style>{`
      @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.7; }
      }

      @keyframes pulseOutline {
        0% {
          border-color: inherit;
          box-shadow: 0 0 0 0 rgba(0,0,0,0);
        }
        30% {
          border-color: #ff3b30;
          box-shadow: 0 0 0 3px rgba(255, 59, 48, 0.25);
        }
        60% {
          border-color: #0a84ff;
          box-shadow: 0 0 0 3px rgba(10, 132, 255, 0.25);
        }
        100% {
          border-color: inherit;
          box-shadow: 0 0 0 0 rgba(0,0,0,0);
        }
      }

      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }

      .ai-pulse {
        animation: pulse 1.5s ease-in-out infinite;
        will-change: opacity;
      }

      .ai-pulse-outline {
        animation: pulseOutline 1.2s ease-in-out 1;
        will-change: border-color, box-shadow;
        border-width: 1px;
        border-radius: 4px;
        border-style: solid;
      }
      .ai-pulse-outline .react-select__control,
      .ai-pulse-outline .MuiInputBase-root {
        animation: pulseOutline 1.2s ease-in-out 1;
        border-width: 1px;
        border-radius: 4px;
        border-style: solid;
      }
      .needs-correction,
      .needs-correction input,
      .needs-correction textarea,
      .needs-correction .react-select__control,
      .needs-correction .MuiInputBase-root {
        border-color: #ff4444 !important;
      }

      .ai-spin {
        animation: spin 1s linear infinite;
        will-change: transform;
      }

      input[type="radio"].radio-input {
        appearance: radio !important;
        -webkit-appearance: radio !important;
        -moz-appearance: radio !important;
        width: 18px !important;
        height: 18px !important;
        margin: 0 !important;
        padding: 0 !important;
        border: none !important;
        background: transparent !important;
        display: inline-block !important;
        visibility: visible !important;
        opacity: 1 !important;
      }
    `}</style>
  </FormContainer>
);

export const DynamicForm: React.FC<DynamicFormProps> = memo(
  ({
    formConfig,
    formData,
    onFieldChange,
    onSubmit,
    onCancel,
    onSave,
    onDraft,
    onEdit,
    onEnhance,
    onDownloadPdf,
    isValid: externalIsValid,
    isSubmitting = false,
    isEnhancing = false,
    isDownloadingPdf = false,
    isSubmitted = false,
    isDraft = false,
    submitError,
    hasPrefilledData = false,
    title,
    customTitle,
    showButtons = true,
    readOnly = false,
    aiState,
    onAICorrection,
    correctionsApplied: externalCorrectionsApplied = false,
    className,
    style,
    submittedAt,
    draftAt,
  }) => {
    const [internalCorrectionsApplied, setInternalCorrectionsApplied] = useState(false);
    const [animationPhase, setAnimationPhase] = useState<AnimationPhase>(AI_ANIMATION.PHASES.IDLE);
    const [_animationTimer, setAnimationTimer] = useState<NodeJS.Timeout | null>(null);

    // Use external correctionsApplied if provided, otherwise use internal state
    const correctionsApplied = externalCorrectionsApplied || internalCorrectionsApplied;

    if (!formConfig) {
      return null;
    }

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      if (!validation.isValid || isSubmitting) return;
      onSubmit?.();
    };

    const handleSave = () => {
      if (!validation.isValid || isSubmitting) return;
      onSave?.();
    };

    const handleDraft = useCallback(() => {
      if (isSubmitting) return;
      onDraft?.();
    }, [isSubmitting, onDraft]);

    const defaultTitle = resolveDefaultTitle(
      customTitle,
      isSubmitted,
      isDraft,
      readOnly,
      hasPrefilledData,
      formConfig
    );

    const sortedFields = [...formConfig.fields].sort((a, b) => a.order - b.order);
    const initializedFormData = { ...formData };
    const validation = validateForm(initializedFormData, sortedFields);
    const finalIsValid = externalIsValid !== undefined ? externalIsValid : validation.isValid;

    const handleFieldChange = useCallback(
      (fieldName: string, value: unknown) => {
        onFieldChange(fieldName, value);
      },
      [onFieldChange]
    );

    // Normalize sections to ensure showInChat is always a boolean (defaults to false)
    const { sections, fieldsBySection, fieldsWithoutSection } = prepareSections(formConfig, sortedFields);

    // Determine if form should be read-only (submitted or draft)
    const isReadOnlyMode = isSubmitted || isDraft || readOnly;

    const renderSectionFields = useCallback(
      (sectionFields: FieldDefinition[], isReadOnlyMode: boolean) => {
        const textareaFields = sectionFields.filter((field) => field.type === 'textarea');
        const otherFields = sectionFields.filter((field) => field.type !== 'textarea');
        const useFullWidthForSingleField = otherFields.length === 1;

        return (
          <>
            {otherFields.map((field) =>
              renderDynamicField(
                field,
                initializedFormData[field.name] as FormFieldValue,
                handleFieldChange,
                isReadOnlyMode,
                validation.fieldErrors[field.name],
                aiState,
                onAICorrection,
                correctionsApplied,
                animationPhase,
                useFullWidthForSingleField
              )
            )}
            {textareaFields.map((field) =>
              renderDynamicField(
                field,
                initializedFormData[field.name] as FormFieldValue,
                handleFieldChange,
                isReadOnlyMode,
                validation.fieldErrors[field.name],
                aiState,
                onAICorrection,
                correctionsApplied,
                animationPhase,
                false // textarea is always full-width
              )
            )}
          </>
        );
      },
      [
        initializedFormData,
        handleFieldChange,
        validation.fieldErrors,
        aiState,
        onAICorrection,
        correctionsApplied,
        animationPhase,
      ]
    );

    // Helper to render fields without sections (legacy behavior)
    const renderFieldsWithoutSections = useCallback(
      (isReadOnlyMode: boolean) => {
        const textareaFields = fieldsWithoutSection.filter((field) => field.type === 'textarea');
        const otherFields = fieldsWithoutSection.filter((field) => field.type !== 'textarea');
        // If there's only one non-textarea field, it should be full-width
        const useFullWidthForSingleField = otherFields.length === 1;

        return (
          <>
            {otherFields.map((field) =>
              renderDynamicField(
                field,
                initializedFormData[field.name] as FormFieldValue,
                handleFieldChange,
                isReadOnlyMode,
                validation.fieldErrors[field.name],
                aiState,
                onAICorrection,
                correctionsApplied,
                animationPhase,
                useFullWidthForSingleField
              )
            )}
            {textareaFields.map((field) =>
              renderDynamicField(
                field,
                initializedFormData[field.name] as FormFieldValue,
                handleFieldChange,
                isReadOnlyMode,
                validation.fieldErrors[field.name],
                aiState,
                onAICorrection,
                correctionsApplied,
                animationPhase,
                false // textarea is always full-width
              )
            )}
          </>
        );
      },
      [
        fieldsWithoutSection,
        initializedFormData,
        handleFieldChange,
        validation.fieldErrors,
        aiState,
        onAICorrection,
        correctionsApplied,
        animationPhase,
      ]
    );

    // Handle AI corrections with animation delay
    useEffect(() => {
      return applyAICorrections(
        aiState,
        correctionsApplied,
        onAICorrection,
        setAnimationPhase,
        setInternalCorrectionsApplied,
        setAnimationTimer
      );
    }, [aiState, correctionsApplied, onAICorrection]);

    const aiProcessingIndicator = getAIProcessingIndicator(aiState);
    const aiCorrectionsSummary = getAICorrectionsSummary(aiState);

    if (isDraft || isSubmitted) {
      return (
        <SubmittedView
          formConfig={formConfig}
          formData={initializedFormData}
          isDraft={isDraft}
          isSubmitted={isSubmitted}
          isSubmitting={isSubmitting}
          showButtons={showButtons}
          onEdit={onEdit}
          onDownloadPdf={onDownloadPdf}
          isDownloadingPdf={isDownloadingPdf}
          submittedAt={submittedAt}
          draftAt={draftAt}
          className={className}
          style={style}
          sections={sections}
          fieldsBySection={fieldsBySection}
          fieldsWithoutSection={fieldsWithoutSection}
        />
      );
    }

    // Render active form
    return (
      <DynamicFormBody
        className={className}
        style={style}
        title={title}
        defaultTitle={defaultTitle}
        aiProcessingIndicator={aiProcessingIndicator}
        handleSubmit={handleSubmit}
        sections={sections}
        fieldsBySection={fieldsBySection}
        renderSectionFields={renderSectionFields}
        isReadOnlyMode={isReadOnlyMode}
        fieldsWithoutSection={fieldsWithoutSection}
        renderFieldsWithoutSections={renderFieldsWithoutSections}
        aiCorrectionsSummary={aiCorrectionsSummary}
        submitError={submitError}
        showButtons={showButtons}
        onSubmit={onSubmit}
        onSave={onSave}
        onDraft={onDraft}
        onCancel={onCancel}
        onEnhance={onEnhance}
        handleSave={handleSave}
        handleDraft={handleDraft}
        isSubmitting={isSubmitting}
        isEnhancing={isEnhancing}
        finalIsValid={finalIsValid}
      />
    );
  }
);

DynamicForm.displayName = 'DynamicForm';

export default DynamicForm;
